import React from 'react'
import Modal from '../shared/Modal'
import { PriceInput } from '../shared/PriceInput'
import { PriceInputWithModifier } from '../shared/PriceInputWithModifier'
import { Aircraft } from '../../types/intellijetnet'
import { AircraftOverride, AircraftOverrideInput, ContainerType, AircraftTransaction } from '../../types/database'
import { AIRCRAFT_STATUS_OPTIONS } from '../../constants/aircraft-status'
import { useOverrideForm } from '../../hooks/useOverrideForm'
import { OverrideFormField } from './override-form/OverrideFormField'

interface OverrideDataModalProps {
  isOpen: boolean
  onClose: () => void
  aircraft: Aircraft
  onSave?: (override: AircraftOverride | null) => void
  containerType?: ContainerType
  transaction?: AircraftTransaction
}

export default function OverrideDataModal({ isOpen, onClose, aircraft, onSave, containerType, transaction }: OverrideDataModalProps) {
  const {
    formData, saving, deleting, backfilling, error,
    hasExistingOverride, hasExistingTransactionOverride,
    availableEMPs, customColumns,
    handleInputChange, handleSubmit, handleDelete, handleBackfillOverrides, handleClose,
  } = useOverrideForm({ isOpen, aircraft, containerType, transaction, onSave, onClose })

  const field = (f: keyof AircraftOverrideInput | 'sale_price' | 'external_sale_price') => ({
    value: formData[f as keyof typeof formData] as any,
    onChange: (v: any) => handleInputChange(f, v),
  })

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Override Data" size="large">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="text-sm text-gray-600 mb-4">
          Override specific data points for Aircraft ID: {aircraft.aircraft_id} ({aircraft.sernbr})
        </div>

        {/* Usage Section */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Usage</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OverrideFormField type="number" label="AFTT (Airframe Total Time)" step={0.01} placeholder="Enter AFTT hours" {...field('aftt')} />
            <OverrideFormField type="number" label="TBO (Time Before Overhaul)" step={0.01} placeholder="Enter TBO hours" {...field('tbo')} />
            <OverrideFormField type="number" label="Landings" placeholder="Enter number of landings" {...field('landings')} />
            <OverrideFormField type="number" label="# PAX (Passenger Capacity)" placeholder="Enter passenger capacity" {...field('pax')} />
            <OverrideFormField type="number" label="Paint Year" min={1900} placeholder="YYYY" {...field('paint_date')} />
            <OverrideFormField type="number" label="Interior Year" min={1900} placeholder="YYYY" {...field('interior_date')} />
          </div>
        </div>

        {/* Location Section */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OverrideFormField type="text" label="Base Airport" placeholder="Enter base airport" {...field('base_airport')} />
            <OverrideFormField type="text" label="Base City" placeholder="Enter base city" {...field('base_city')} />
            <OverrideFormField type="text" label="Base State" placeholder="Enter base state" {...field('base_state')} />
            <OverrideFormField type="text" label="Base Country" placeholder="Enter base country" {...field('base_country')} />
          </div>
        </div>

        {/* Market Status Section */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Market Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OverrideFormField type="boolean-select" label="For Sale" {...field('for_sale')} />
            <OverrideFormField type="boolean-select" label="Leased" {...field('leased')} />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="font-semibold">Actual</span> Ask Price
                </label>
                <button
                  type="button"
                  onClick={() => handleInputChange('actual_ask_make_offer', !formData.actual_ask_make_offer)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${formData.actual_ask_make_offer ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Make Offer
                </button>
              </div>
              <PriceInput value={formData.asking_price} onChange={(v) => handleInputChange('asking_price', v)} placeholder="e.g., 1.234 for $1.234M" disabled={formData.actual_ask_make_offer === true} />
            </div>
            <PriceInputWithModifier value={formData.take_price} modifier={formData.take_price_modifier} onValueChange={(v) => handleInputChange('take_price', v)} onModifierChange={(m) => handleInputChange('take_price_modifier', m)} placeholder="e.g., 1.234 for $1.234M" label={<><span className="font-semibold">Actual</span> Take Price</>} />
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span className="font-semibold">Adjusted</span> Ask Price
                </label>
                <button
                  type="button"
                  onClick={() => handleInputChange('adj_ask_make_offer', !formData.adj_ask_make_offer)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${formData.adj_ask_make_offer ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Make Offer
                </button>
              </div>
              <PriceInput value={formData.external_asking_price} onChange={(v) => handleInputChange('external_asking_price', v)} placeholder="e.g., 1.234 for $1.234M" disabled={formData.adj_ask_make_offer === true} />
            </div>
            <PriceInputWithModifier value={formData.external_take_price} modifier={formData.external_take_price_modifier} onValueChange={(v) => handleInputChange('external_take_price', v)} onModifierChange={(m) => handleInputChange('external_take_price_modifier', m)} placeholder="e.g., 1.234 for $1.234M" label={<><span className="font-semibold">Adjusted</span> Take Price</>} />
            {containerType === 'sold' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><span className="text-green-600 font-semibold">Internal</span> Sold Price</label>
                  <PriceInput value={formData.sale_price} onChange={(v) => handleInputChange('sale_price', v)} placeholder="e.g., 1.234 for $1.234M" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><span className="text-red-600 font-semibold">External</span> Sold Price</label>
                  <PriceInput value={formData.external_sale_price} onChange={(v) => handleInputChange('external_sale_price', v)} placeholder="e.g., 1.234 for $1.234M" />
                </div>
              </>
            )}
            <OverrideFormField type="select" label="Status" colSpan={2} placeholder="Select status..." options={AIRCRAFT_STATUS_OPTIONS.map(o => ({ value: o.value, label: o.label }))} {...field('status')} />
          </div>
        </div>

        {/* Maintenance Section */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <OverrideFormField type="select" label="Engine Maintenance Program (EMP)" placeholder="-- Select EMP --" options={availableEMPs.map(e => ({ value: e.name, label: e.name }))} {...field('emp')} />
              {availableEMPs.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">No EMPs configured for this market. Contact an admin to add EMPs.</p>
              )}
            </div>
            <OverrideFormField type="text" label="Maintenance Program" placeholder="Enter maintenance program" {...field('maintenance_program')} />
            <OverrideFormField type="select" label="Damage History" options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }, { value: 'Unknown', label: 'Unknown' }]} {...field('damage_history')} />
          </div>
        </div>

        {/* Important Dates Section */}
        <div className="pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Important Dates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OverrideFormField type="date" label="Date Listed" {...field('date_listed')} />
            <OverrideFormField type="date" label="Date Purchased" {...field('date_purchased')} />
            <OverrideFormField type="number" label="Year Manufactured" min={1900} max={2100} placeholder="e.g., 2020" {...field('year_manufactured')} />
            <OverrideFormField type="number" label="Year Delivered" min={1900} max={2100} placeholder="e.g., 2021" {...field('year_delivered')} />
          </div>
        </div>

        {/* Custom Columns Section */}
        {customColumns.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Custom Fields</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customColumns.map((column) => {
                const key = `custom_${column.column_index}_value` as keyof AircraftOverrideInput
                return column.input_type === 'text' ? (
                  <OverrideFormField key={column.id} type="text" label={column.column_title} placeholder={`Enter ${column.column_title.toLowerCase()}`} value={formData[key] as string} onChange={(v) => handleInputChange(key, v)} />
                ) : (
                  <OverrideFormField key={column.id} type="select" label={column.column_title} placeholder={`Select ${column.column_title.toLowerCase()}`} options={(column.options || []).map(o => ({ value: o, label: o }))} value={formData[key] as string} onChange={(v) => handleInputChange(key, v)} />
                )
              })}
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          {/* Action buttons on the left */}
          <div className="flex space-x-3">
            {/* Backfill button for sold aircraft (only show if transaction exists and has missing override data) */}
            {containerType === 'sold' && transaction && (
              <button
                type="button"
                onClick={handleBackfillOverrides}
                disabled={saving || deleting || backfilling}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
              >
                {backfilling && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {backfilling ? 'Backfilling...' : 'Backfill Override Data'}
              </button>
            )}

            {/* Delete button (only show if override exists) */}
            {(hasExistingOverride || (containerType === 'sold' && hasExistingTransactionOverride)) && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving || deleting || backfilling}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center"
              >
                {deleting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {deleting ? 'Deleting...' : 'Delete Override Data'}
              </button>
            )}
          </div>

          {/* Cancel and Save buttons on the right */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving || deleting || backfilling}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || deleting || backfilling}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-950 border border-transparent rounded-md hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 flex items-center"
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {saving ? 'Saving...' : 'Save Override Data'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
